import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';

import {
  SkillAiDateRangeParams,
  SkillAiStatisticsSummaryResponse,
  SkillAiTimeseriesItem,
  SkillAiTopUserItem,
  SkillAiTopSpecializationItem,
  SkillAiLogsResponse,
  SkillAiLogsQueryParams,
  SkillAiBudgetStatusApiResponse,
  SkillAiBudgetStatusResponse,
  SkillAiBudgetTopUserItem,
  SkillAiBudgetTokensApiResponse,
  StandardAiQuestionStatisticsResponse,
  StandardAiQuestionStatisticsRequest,
  SkillLearningPopularityPageResponse,
  SkillLearningPopularityRequest,
  ContentTranslationCoverageResponse,
} from '../models/skill-learning.model';
import { SkillAiStatisticsApiService } from './skill-ai-statistics-api.service';

@Injectable()
export class SkillAiStatisticsFacadeService {

  constructor(private api: SkillAiStatisticsApiService) {}

  fetchSummary(params: SkillAiDateRangeParams): Observable<SkillAiStatisticsSummaryResponse> {
    return this.api.fetchSummary(params);
  }

  fetchTimeseries(params: SkillAiDateRangeParams): Observable<SkillAiTimeseriesItem[]> {
    return this.api.fetchTimeseries(params);
  }

  fetchTopUsers(params: SkillAiDateRangeParams, limit: number): Observable<SkillAiTopUserItem[]> {
    return this.api.fetchTopUsers(params, limit);
  }

  fetchTopSpecializations(params: SkillAiDateRangeParams, limit: number): Observable<SkillAiTopSpecializationItem[]> {
    return this.api.fetchTopSpecializations(params, limit);
  }

  fetchLogs(params: SkillAiLogsQueryParams): Observable<SkillAiLogsResponse> {
    return this.api.fetchLogs(params);
  }

  fetchBudgetStatus(): Observable<SkillAiBudgetStatusResponse> {
    return this.api.fetchBudgetStatus()
      .map(response => this.normalizeBudgetStatus(response));
  }

  fetchStandardQuestionStatistics(
    params: StandardAiQuestionStatisticsRequest,
  ): Observable<StandardAiQuestionStatisticsResponse> {
    return this.api.fetchStandardQuestionStatistics(params);
  }

  fetchLearningPopularityStatistics(
    params: SkillLearningPopularityRequest,
  ): Observable<SkillLearningPopularityPageResponse> {
    return this.api.fetchLearningPopularityStatistics(params);
  }

  fetchTranslationCoverageStatistics(): Observable<ContentTranslationCoverageResponse> {
    return this.api.fetchTranslationCoverageStatistics();
  }

  private normalizeBudgetStatus(response: SkillAiBudgetStatusApiResponse): SkillAiBudgetStatusResponse {
    const globalTokens = this.resolveGlobalTokens(response);
    return {
      globalTokensUsed: globalTokens.used,
      globalTokenLimit: globalTokens.limit,
      usagePercentage: globalTokens.percent,
      status: this.resolveStatus(response, globalTokens),
      testGenerationRequestsToday: response.testGenerationRequestsToday || 0,
      topUsers: this.resolveTopUsers(response),
    };
  }

  private resolveGlobalTokens(response: SkillAiBudgetStatusApiResponse): SkillAiBudgetTokensApiResponse {
    if (response && response.globalTokens) {
      return response.globalTokens;
    }
    return this.createEmptyGlobalTokens();
  }

  private resolveStatus(
    response: SkillAiBudgetStatusApiResponse,
    globalTokens: SkillAiBudgetTokensApiResponse,
  ): string {
    if (response && response.globalStatus) {
      return response.globalStatus;
    }
    return globalTokens.status || 'normal';
  }

  private resolveTopUsers(response: SkillAiBudgetStatusApiResponse): SkillAiBudgetTopUserItem[] {
    if (response && response.topUsers) {
      return response.topUsers;
    }
    return [];
  }

  private createEmptyGlobalTokens(): SkillAiBudgetTokensApiResponse {
    return {
      used: 0,
      limit: 0,
      percent: 0,
      status: 'normal',
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/skill-ai-statistics-facade.service.ts